import type { Disposable } from "@/shared/contracts/lifecycle";
import type { HoverPointerModel } from "./hoverPointerModel";

const HOVER_ANCHOR_TARGET_ID_STORAGE_KEY =
  "hover-idle:last-hover-anchor-target-id";

export interface HoverAnchorStorage {
  load(): string | null;
  save(targetId: string): void;
  clear(): void;
}

interface HoverAnchorPersistenceControllerOptions {
  readonly enabled: boolean;
  readonly model: HoverPointerModel;
  readonly storage: HoverAnchorStorage;
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function createLocalStorageHoverAnchorStorage(
  storage: StorageLike | null = resolveDefaultStorage(),
): HoverAnchorStorage {
  return {
    load(): string | null {
      if (storage === null) {
        return null;
      }
      try {
        return normalizeTargetId(
          storage.getItem(HOVER_ANCHOR_TARGET_ID_STORAGE_KEY),
        );
      } catch {
        return null;
      }
    },
    save(targetId: string): void {
      if (storage === null) {
        return;
      }
      const normalized = normalizeTargetId(targetId);
      if (normalized === null) {
        this.clear();
        return;
      }
      try {
        storage.setItem(HOVER_ANCHOR_TARGET_ID_STORAGE_KEY, normalized);
      } catch {
        // Storage is best-effort UI convenience state; ignore quota/privacy errors.
      }
    },
    clear(): void {
      if (storage === null) {
        return;
      }
      try {
        storage.removeItem(HOVER_ANCHOR_TARGET_ID_STORAGE_KEY);
      } catch {
        // Storage is best-effort UI convenience state; ignore quota/privacy errors.
      }
    },
  };
}

export function createHoverAnchorPersistenceController(
  options: HoverAnchorPersistenceControllerOptions,
): Disposable {
  if (!options.enabled) {
    return { dispose: () => undefined };
  }

  const savedTargetId = options.storage.load();
  if (savedTargetId !== null) {
    options.model.setHoverAnchorTargetId(savedTargetId);
  }

  let lastStoredTargetId = savedTargetId;
  const unsubscribe = options.model.subscribe((snapshot) => {
    const nextTargetId = snapshot.hoverAnchorTargetId;
    if (nextTargetId === lastStoredTargetId) {
      return;
    }
    lastStoredTargetId = nextTargetId;
    if (nextTargetId === null) {
      options.storage.clear();
    } else {
      options.storage.save(nextTargetId);
    }
  });

  return { dispose: unsubscribe };
}

function normalizeTargetId(value: string | null): string | null {
  if (value === null) {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function resolveDefaultStorage(): StorageLike | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}
